/**
 * Every router path this app declares, as plain strings.
 *
 * This module is deliberately free of React and component imports so tests can
 * import it without pulling in the app. `index.tsx` builds the router purely
 * from these constants and `tools-registry.spec.ts` asserts that every home
 * launcher card points at one of them, so a card can never again link to a
 * route that does not exist.
 *
 * Adding a route means adding it here first.
 */
export const ROUTES = {
  /** Login screen; also served at the app root. */
  LOGIN: "/login",
  /** App root, which renders the login screen. */
  ROOT: "/",
  /** The internal tools launcher. */
  HOME: "/home",

  /** Legacy schema-matching schema editor, with no schema selected. */
  DOC_INGESTION_SCHEMA_EDITOR: "/doc-ingestion/schema-editor",
  /** Legacy schema-matching schema editor, focused on one schema. */
  DOC_INGESTION_SCHEMA_EDITOR_DETAIL:
    "/doc-ingestion/schema-editor/:schemaUuid",
  /** Legacy schema-matching schema list. */
  DOC_INGESTION_SCHEMAS: "/doc-ingestion/schemas",
  /** Outlook mailbox integrations that feed document ingestion. */
  DOC_INGESTION_OUTLOOK_INTEGRATIONS: "/doc-ingestion/outlook-integrations",
  /** Document ingestion volume statistics. */
  DOC_INGESTION_STATISTICS: "/doc-ingestion/statistics",
  /** Document ingestion scan logs. */
  DOC_INGESTION_LOGS: "/doc-ingestion/logs",
  /** Schema test list lookup form. */
  DOC_INGESTION_SCHEMA_TEST_LISTS: "/doc-ingestion/schema-test-lists",
  /** One schema test list's results. */
  DOC_INGESTION_SCHEMA_TEST_LIST_DETAIL:
    "/doc-ingestion/schema-test-lists/:uuid",
  /** One schema run's extracted fields and anchor matches. */
  DOC_INGESTION_SCHEMA_RUN_DETAIL: "/doc-ingestion/schema-runs/:uuid",

  /** Redirect shim for the retired `/docs` tab hub. */
  LEGACY_DOCS: "/docs",
  /** Redirect shim for the retired top-level schema test list lookup. */
  LEGACY_SCHEMA_TEST_LISTS: "/schema-test-lists",
  /** Redirect shim for the retired top-level schema test list detail. */
  LEGACY_SCHEMA_TEST_LIST_DETAIL: "/schema-test-lists/:uuid",
  /** Redirect shim for the retired top-level schema run detail. */
  LEGACY_SCHEMA_RUN_DETAIL: "/schema-runs/:uuid",

  /** Document-extraction eval sets and runs. */
  DOC_EXTRACTION_EVALS: "/doc-extraction-evals",
  /** One document-extraction eval run's detail. */
  DOC_EXTRACTION_EVAL_RUN: "/doc-extraction-evals/runs/:id",
  /** Zero-LLM prefill verdict inspector. */
  INGESTION_INSPECTOR: "/ingestion-inspector",
  /** Ad-hoc document-extraction preview for one document. */
  ADHOC_INGESTION_PREVIEW: "/adhoc-ingestion-preview",
  /** Ad-hoc document-extraction preview for a batch of documents. */
  ADHOC_INGESTION_PREVIEW_BATCH: "/adhoc-ingestion-preview-batch",
  /** Ingestion test-run lookup form. */
  INGESTION_TEST_RUNS: "/ingestion-test-runs",
  /** One ingestion test run's debug pane. */
  INGESTION_TEST_RUN_DETAIL: "/ingestion-test-runs/:id",

  /** EDI transaction sender and viewer. */
  EDI: "/edi",
  /** EDI transactions by company. */
  EDI_VIEW: "/edi/view",
  /** Invoice email transmission logs. */
  INVOICE_TRANSMISSIONS: "/invoice-transmissions",

  /** Backend boilerplate generator. */
  CODE_GEN: "/code-gen",
  /** Bulk barcode generator. */
  BARCODE_GENERATOR: "/barcode-generator",
  /** Render deploys by environment. */
  RENDER: "/render",
  /** Outbound SFTP uploads. */
  SFTP_UPLOADS: "/sftp-uploads",
  /** Background job queue dashboard. */
  QUEUES: "/queues",

  /** Test orders list. */
  TEST_ORDERS: "/test-orders",
  /** Test order creation form. */
  TEST_ORDER_CREATE: "/test-orders/create",
  /** Test order edit form. */
  TEST_ORDER_EDIT: "/test-orders/:uuid",

  /** Per-company operational settings. */
  COMPANY_CONFIGURATIONS: "/company-configurations",
  /** Cross-environment configuration copy. */
  CONFIG_MIGRATION: "/config-migration",
  /** Notification engine cutover controls. */
  NOTIFICATION_CUTOVER: "/notification-cutover",
  /** Shadow-mode comparisons between the legacy path and the engine. */
  NOTIFICATION_SHADOW: "/notification-shadow",

  /** Crown data import hub; its modules are nested child routes. */
  CROWN_DATA_IMPORT: "/crown-data-import",
  /** Redirect shim for the standalone Crown charges validator. */
  CROWN_CHARGES_VALIDATOR: "/crown-charges-validator",
  /** Redirect shim for the standalone Crown contacts importer. */
  CONTACTS_IMPORTER: "/contacts-importer",
  /** Redirect shim for the standalone Crown tariff importer. */
  TARIFF_IMPORTER: "/tariff-importer",
  /** Redirect shim for the standalone Crown accessorial importer. */
  ACCESSORIAL_IMPORTER: "/accessorial-importer",
  /** Redirect shim for the standalone Crown orders importer. */
  ORDERS_IMPORTER: "/orders-importer",
  /** Redirect shim for the standalone Crown users importer. */
  USERS_IMPORTER: "/users-importer",
  /** Redirect shim for the standalone Crown drivers importer. */
  DRIVERS_IMPORTER: "/drivers-importer",

  /** Piece backfill for packages that predate piece tracking. */
  PIECE_BACKFILL: "/piece-backfill",

  /** External Storybook redirect. */
  STORYBOOK: "/storybook",

  /** External API key administration list. */
  API_KEYS_ADMIN: "/admin/api-keys",
  /** One API key's detail. */
  API_KEY_ADMIN_DETAIL: "/admin/api-keys/:keyUuid",
  /** One API key's webhook payload mapping editor. */
  API_KEY_PAYLOAD_MAPPING: "/admin/api-keys/:keyUuid/payload-mapping",

  /** Help documents shown to customers on Settings > Documentation. */
  DOCUMENTATION_ADMIN: "/admin/documentation",

  /** Messages from Tenet to one company or to every company. */
  ORGANIZATION_MESSAGES: "/admin/organization-messages",

  /** Order mirroring administration (targets, capture, sync, outbox). */
  ORDER_MIRROR: "/admin/order-mirror",

  /** Synthetic EDI and API order creation for a target test environment. */
  TEST_INTEGRATION_ORDERS: "/admin/test-integration-orders",
  OUTBOUND_TEST_BENCH: "/admin/outbound-test-bench",

  /** Carrier and customer EDI identity and Tenedi partnerships, per connection. */
  TENEDI_CONNECTIONS: "/admin/tenedi-connections",

  /** Ends live customer sessions when an account is shut off. */
  USER_SESSIONS_ADMIN: "/admin/user-sessions",

  /** Every SMS number we hold, its consent status and history, masked. */
  SMS_CONSENT_ADMIN: "/admin/sms-consent",
} as const;

/** Every path the router declares, in no particular order. */
export const ALL_ROUTE_PATHS: readonly string[] = Object.values(ROUTES);

/**
 * Path of the schema editor focused on `schemaUuid`.
 *
 * @param schemaUuid - The schema to open in the editor.
 */
export const schemaEditorPath = (schemaUuid: string): string =>
  `${ROUTES.DOC_INGESTION_SCHEMA_EDITOR}/${schemaUuid}`;

/**
 * Path of a single schema run's results.
 *
 * @param uuid - The schema run uuid.
 */
export const schemaRunPath = (uuid: string): string =>
  ROUTES.DOC_INGESTION_SCHEMA_RUN_DETAIL.replace(":uuid", uuid);

/**
 * Path of a single schema test list's results.
 *
 * @param uuid - The schema run test list uuid.
 */
export const schemaTestListPath = (uuid: string): string =>
  `${ROUTES.DOC_INGESTION_SCHEMA_TEST_LISTS}/${uuid}`;
